
import "vanilla-cookieconsent/dist/cookieconsent.css";
import * as CookieConsent from "vanilla-cookieconsent";

const CAT_NECESSARY = "necessary";
const CAT_ANALYTICS = "analytics";
const CAT_ADVERTISEMENT = "advertisement";
const CAT_FUNCTIONALITY = "functionality";
const CAT_SECURITY = "security";

const SERVICE_AD_STORAGE = 'ad_storage'
const SERVICE_AD_USER_DATA = 'ad_user_data'
const SERVICE_AD_PERSONALIZATION = 'ad_personalization'
const SERVICE_ANALYTICS_STORAGE = 'analytics_storage'
const SERVICE_FUNCTIONALITY_STORAGE = 'functionality_storage'
const SERVICE_PERSONALIZATION_STORAGE = 'personalization_storage'
const SERVICE_SECURITY_STORAGE = 'security_storage'

// Define dataLayer and the gtag function.
window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}

// Set default consent to 'denied' (this should happen before changing any other dataLayer)
gtag('consent', 'default', {
    [SERVICE_AD_STORAGE]: 'denied',
    [SERVICE_AD_USER_DATA]: 'denied',
    [SERVICE_AD_PERSONALIZATION]: 'denied',
    [SERVICE_ANALYTICS_STORAGE]: 'denied',
    [SERVICE_FUNCTIONALITY_STORAGE]: 'denied',
    [SERVICE_PERSONALIZATION_STORAGE]: 'denied',
    [SERVICE_SECURITY_STORAGE]: 'denied',
});

/**
 * Update gtag consent according to the users choices made in CookieConsent UI
 */
function updateGtagConsent() {
    gtag('consent', 'update', {
        [SERVICE_ANALYTICS_STORAGE]: CookieConsent.acceptedService(SERVICE_ANALYTICS_STORAGE, CAT_ANALYTICS) ? 'granted' : 'denied',
        [SERVICE_AD_STORAGE]: CookieConsent.acceptedService(SERVICE_AD_STORAGE, CAT_ADVERTISEMENT) ? 'granted' : 'denied',
        [SERVICE_AD_USER_DATA]: CookieConsent.acceptedService(SERVICE_AD_USER_DATA, CAT_ADVERTISEMENT) ? 'granted' : 'denied',
        [SERVICE_AD_PERSONALIZATION]: CookieConsent.acceptedService(SERVICE_AD_PERSONALIZATION, CAT_ADVERTISEMENT) ? 'granted' : 'denied',
        [SERVICE_FUNCTIONALITY_STORAGE]: CookieConsent.acceptedService(SERVICE_FUNCTIONALITY_STORAGE, CAT_FUNCTIONALITY) ? 'granted' : 'denied',
        [SERVICE_PERSONALIZATION_STORAGE]: CookieConsent.acceptedService(SERVICE_PERSONALIZATION_STORAGE, CAT_FUNCTIONALITY) ? 'granted' : 'denied',
        [SERVICE_SECURITY_STORAGE]: CookieConsent.acceptedService(SERVICE_SECURITY_STORAGE, CAT_SECURITY) ? 'granted' : 'denied',
    });
}

export default {
    install: (app, pluginConfig) => {
        app.provide('CookieConsent', CookieConsent);
        CookieConsent.run({
            guiOptions: {
                consentModal: {
                    layout: "box inline",
                    position: "bottom left",
                    equalWeightButtons: true,
                    flipButtons: false
                },
                preferencesModal: {
                    layout: "bar",
                    position: "left",
                    equalWeightButtons: true,
                    flipButtons: true
                }
            },
            onFirstConsent: () => {
                updateGtagConsent();
            },
            onConsent: () => {
                updateGtagConsent();
            },
            onChange: () => {
                updateGtagConsent();
            },

            // Configure categories and services
            categories: {
                [CAT_NECESSARY]: {
                    enabled: true,  // this category is enabled by default
                    readOnly: true,  // this category cannot be disabled
                },
                [CAT_ANALYTICS]: {
                    autoClear: {
                        cookies: [
                            {
                                name: /^_ga/,   // regex: match all cookies starting with '_ga'
                            },
                            {
                                name: '_gid',   // string: exact cookie name
                            }
                        ]
                    },
                    // See: https://cookieconsent.orestbida.com/reference/configuration-reference.html#category-services
                    services: {
                        [SERVICE_ANALYTICS_STORAGE]: {
                            label: 'Enables storage (such as cookies) related to analytics e.g. visit duration.',
                        }
                    }
                },
                [CAT_ADVERTISEMENT]: {
                    services: {
                        [SERVICE_AD_STORAGE]: {
                            label: 'Enables storage (such as cookies) related to advertising.',
                        },
                        [SERVICE_AD_USER_DATA]: {
                            label: 'Sets consent for sending user data related to advertising to Google.',
                        },
                        [SERVICE_AD_PERSONALIZATION]: {
                            label: 'Sets consent for personalized advertising.',
                        },
                    }
                },
                [CAT_FUNCTIONALITY]: {
                    services: {
                        [SERVICE_FUNCTIONALITY_STORAGE]: {
                            label: 'Enables storage that supports the functionality of the website or app e.g. language settings.',
                        },
                        [SERVICE_PERSONALIZATION_STORAGE]: {
                            label: 'Enables storage related to personalization e.g. video recommendations.',
                        },
                    }
                },
                [CAT_SECURITY]: {
                    services: {
                        [SERVICE_SECURITY_STORAGE]: {
                            label: 'Enables storage related to security such as authentication functionality, fraud prevention, and other user protection.',
                        },
                    }
                }
            },

            language: {
                default: 'cs',
                translations: {
                    en: {
                        consentModal: {
                            title: "We use cookies",
                            description: "To keep Roori running and improving, we use cookies. If you wish to adjust your preferences, click the 'Manage settings' button.",
                            acceptAllBtn: "Accept all cookies",
                            acceptNecessaryBtn: "Reject non-essential",
                            showPreferencesBtn: "Manage settings"
                        },
                        preferencesModal: {
                            title: "Manage cookie preferences",
                            acceptAllBtn: "Accept all",
                            acceptNecessaryBtn: "Reject all",
                            savePreferencesBtn: "Accept current selection",
                            closeIconLabel: "Close modal",
                            sections: [
                                {
                                    title: "Cookie usage",
                                    description: "We use cookies to ensure the basic functionalities of the website and to enhance your online experience."
                                },
                                {
                                    title: "Strictly necessary cookies",
                                    description: "These cookies are essential for the proper functioning of the website, for example for user authentication.",
                                    linkedCategory: CAT_NECESSARY
                                },
                                {
                                    title: "Analytics",
                                    description: "Cookies used for analytics help collect data that allows services to understand how users interact with a particular service. These insights allow services both to improve content and to build better features that improve the user’s experience.",
                                    linkedCategory: CAT_ANALYTICS,
                                    cookieTable: {
                                        headers: {
                                            name: "Name",
                                            domain: "Service",
                                            description: "Description",
                                            expiration: "Expiration"
                                        },
                                        body: [
                                            {
                                                name: "_ga",
                                                domain: "Google Analytics",
                                                description: "Cookie set by <a href=\"https://business.safety.google/adscookies/\">Google Analytics</a>",
                                                expiration: "Expires after 12 days"
                                            },
                                            {
                                                name: "_gid",
                                                domain: "Google Analytics",
                                                description: "Cookie set by <a href=\"https://business.safety.google/adscookies/\">Google Analytics</a>",
                                                expiration: "Session"
                                            }
                                        ]
                                    }
                                },
                                {
                                    title: "Advertising",
                                    description: "Google uses cookies for advertising, including serving and rendering ads, personalizing ads (depending on your ad settings at <a href=\"https://g.co/adsettings\">g.co/adsettings</a>), limiting the number of times an ad is shown to a user, muting ads you have chosen to stop seeing, and measuring the effectiveness of ads.",
                                    linkedCategory: CAT_ADVERTISEMENT
                                },
                                {
                                    title: "Functionality",
                                    description: "Cookies used for functionality allow users to interact with a service or site to access features that are fundamental to that service. Things considered fundamental to the service include preferences like the user’s choice of language, product optimizations that help maintain and improve a service, and maintaining information relating to a user’s session, such as the content of a shopping cart.",
                                    linkedCategory: CAT_FUNCTIONALITY
                                },
                                {
                                    title: "Security",
                                    description: "Cookies used for security authenticate users, prevent fraud, and protect users as they interact with a service.",
                                    linkedCategory: CAT_SECURITY
                                },
                                {
                                    title: "More information",
                                    description: "For more information on how we process personal data, please contact us at <a href=\"mailto:privacy@maeum.one\">privacy@maeum.one</a>."
                                }
                            ]
                        }
                    },
                    ko: {
                        consentModal: {
                            title: "쿠키 사용 안내",
                            description: "Roori의 서비스를 제공하고 개선하기 위해 쿠키를 사용합니다. 설정을 변경하려면 '설정 관리' 버튼을 클릭하세요.",
                            acceptAllBtn: "모든 쿠키 허용",
                            acceptNecessaryBtn: "필수 외 쿠키 거부",
                            showPreferencesBtn: "설정 관리"
                        },
                        preferencesModal: {
                            title: "쿠키 설정 관리",
                            acceptAllBtn: "모든 쿠키 허용",
                            acceptNecessaryBtn: "필수 외 쿠키 거부",
                            savePreferencesBtn: "선택 내용 저장",
                            closeIconLabel: "닫기",
                            sections: [
                                {
                                    title: "쿠키 사용 안내",
                                    description: "웹사이트의 기본 기능을 보장하고 온라인 경험을 향상시키기 위해 쿠키를 사용합니다."
                                },
                                {
                                    title: "필수 쿠키",
                                    description: "이 쿠키는 웹사이트의 필수 기능(예: 사용자 인증)을 위해 필요합니다.",
                                    linkedCategory: CAT_NECESSARY
                                },
                                {
                                    title: "분석 쿠키",
                                    description: "이 쿠키는 사용자의 서비스 이용 방식에 대한 데이터를 수집하여 콘텐츠 개선 및 기능 향상에 도움을 줍니다.",
                                    linkedCategory: CAT_ANALYTICS
                                },
                                {
                                    title: "광고 쿠키",
                                    description: "Google은 광고 제공, 개인 맞춤 광고, 광고 노출 횟수 제한 등에 쿠키를 사용합니다.",
                                    linkedCategory: CAT_ADVERTISEMENT
                                },
                                {
                                    title: "기능 쿠키",
                                    description: "이 쿠키는 언어 설정 등 사용자 기본 환경을 기억하여 더 나은 서비스를 제공합니다.",
                                    linkedCategory: CAT_FUNCTIONALITY
                                },
                                {
                                    title: "보안 쿠키",
                                    description: "이 쿠키는 사용자 인증, 사기 방지 및 사용자 보호 기능을 지원합니다.",
                                    linkedCategory: CAT_SECURITY
                                },
                                {
                                    title: "자세한 정보",
                                    description: "개인정보 처리 방식에 대한 자세한 내용은 <a href=\"mailto:privacy@maeum.one\">privacy@maeum.one</a>으로 문의해 주세요."
                                }
                            ]
                        }
                    },
                    cs: {
                        consentModal: {
                            title: "Využíváme cookies",
                            description: "Abychom mohli nabízet Roori a neustále jej zlepšovat, využíváme cookies. Pokud chcete toto nastavení upravit, klikněte na tlačítko 'Upravit nastavení'.",
                            acceptAllBtn: "Potvrzuji všechny cookies",
                            acceptNecessaryBtn: "Zamítám nepovinné",
                            showPreferencesBtn: "Upravit nastavení"
                        },
                        preferencesModal: {
                            title: "Správa nastavení cookies",
                            acceptAllBtn: "Přijmout vše",
                            acceptNecessaryBtn: "Odmítnout vše",
                            savePreferencesBtn: "Přijmout vybrané",
                            closeIconLabel: "Zavřít okno",
                            sections: [
                                {
                                    title: "Používání cookies",
                                    description: "Používáme cookies k zajištění základních funkcí webu a ke zlepšení vašeho online zážitku."
                                },
                                {
                                    title: "Nezbytné cookies",
                                    description: "Tyto cookies jsou nezbytné pro správné fungování webu, například pro ověřování uživatelů.",
                                    linkedCategory: CAT_NECESSARY
                                },
                                {
                                    title: "Analytické cookies",
                                    description: "Cookies používané pro analýzu pomáhají shromažďovat data, která umožňují službám pochopit, jak uživatelé interagují s danou službou. Tyto poznatky umožňují službám zlepšovat obsah a vytvářet lepší funkce, které zlepšují uživatelský zážitek.",
                                    linkedCategory: CAT_ANALYTICS,
                                    cookieTable: {
                                        headers: {
                                            name: "Název",
                                            domain: "Služba",
                                            description: "Popis",
                                            expiration: "Platnost"
                                        },
                                        body: [
                                            {
                                                name: "_ga",
                                                domain: "Google Analytics",
                                                description: "Cookie nastavené službou <a href=\"https://business.safety.google/adscookies/\">Google Analytics</a>",
                                                expiration: "Platí 12 dní"
                                            },
                                            {
                                                name: "_gid",
                                                domain: "Google Analytics",
                                                description: "Cookie nastavené službou <a href=\"https://business.safety.google/adscookies/\">Google Analytics</a>",
                                                expiration: "Relace"
                                            }
                                        ]
                                    }
                                },
                                /*{
                                    title: "Reklamní cookies",
                                    description: "Google používá cookies pro reklamu, včetně zobrazování a podávání reklam, personalizace reklam (v závislosti na vašem nastavení reklam na <a href=\"https://g.co/adsettings\">g.co/adsettings</a>), omezení počtu zobrazení reklamy uživateli, ztlumení reklam, které jste si vybrali přestat vidět, a měření účinnosti reklam.",
                                    linkedCategory: CAT_ADVERTISEMENT
                                },
                                {
                                    title: "Funkční cookies",
                                    description: "Cookies používané pro funkčnost umožňují uživatelům interagovat se službou nebo webem a přistupovat k funkcím, které jsou pro službu zásadní. Mezi zásadní věci patří například preference jako volba jazyka uživatele, optimalizace produktů, které pomáhají udržovat a zlepšovat službu, a uchování informací o uživatelské relaci, například obsah nákupního košíku.",
                                    linkedCategory: CAT_FUNCTIONALITY
                                },
                                {
                                    title: "Bezpečnostní cookies",
                                    description: "Cookies používané pro bezpečnost ověřují uživatele, zabraňují podvodům a chrání uživatele při interakci se službou.",
                                    linkedCategory: CAT_SECURITY
                                },*/
                                {
                                    title: "Další informace",
                                    description: "Pokud máte zájem o více informací o tom, jak zpracováváme osobní údaje, kontaktujte nás na e-mailu <a href=\"mailto:privacy@maeum.one\">privacy@maeum.one</a>."
                                }
                            ]
                        }
                    }
                }
            }
        });
    }
}